import React, {
  CSSProperties,
  FC,
  PropsWithChildren,
  useCallback,
  useState,
} from 'react';
import styled from 'styled-components';
import { colorBgContainer, colorGray } from '@atlas-frontend/design-tokens';
import {
  EmptyData,
  InfoIconUrl,
  InfoIcon,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Padding,
  Stack,
  TextEllipsis,
} from '../common/components';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './DashboardPanel.translations.json';
import * as Icon from 'react-feather';
import { Button, Flex } from 'antd';

const { addTranslations, t } = i18nNsFactory('dashboard-panel');
addTranslations(translations);

interface DashboardPanelProps extends PropsWithChildren {
  title: string;
  icon?: React.ReactNode;
  infoIconUrl?: InfoIconUrl;
  isLoading?: boolean;
  isEmpty?: boolean;
  showMaximize?: boolean;
  hideEmptyImage?: boolean;
  style?: CSSProperties;
  modalContent?: React.ReactNode;
  additionalHeaderContent?: React.ReactNode;
}

export const DashboardPanel: FC<DashboardPanelProps> = (props) => {
  const {
    title,
    icon,
    infoIconUrl,
    isLoading = false,
    isEmpty = false,
    showMaximize = true,
    hideEmptyImage = true,
    style,
    modalContent,
    children,
    additionalHeaderContent,
  } = props;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const showModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);
  const handleClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const showEmptyData = (isEmpty || !children) && !isLoading;

  return (
    <ContentWrapper
      className="dashboard-panel"
      id="dashboard-panel-content"
      size="xs"
      style={style}
    >
      <StyledHeader vertical={12} horizontal>
        <Flex gap="small" align="center" justify="space-between">
          <Flex gap="small" align="center" justify="space-between">
            {icon}
            <h4>{title}</h4>
            <InfoIcon infoIconUrl={infoIconUrl}></InfoIcon>
          </Flex>
          <Flex gap="small" align="center" justify="space-between">
            {additionalHeaderContent}
            {showMaximize && (
              <Button
                id="open-modal"
                title={t('button.open')}
                onClick={showModal}
                type="link"
                icon={<Icon.Maximize2 size={18} color={colorGray[6]} />}
              />
            )}
          </Flex>
        </Flex>
      </StyledHeader>
      <Padding horizontal scrollable>
        {showEmptyData ? (
          <EmptyData hideImage={hideEmptyImage}>
            {t('emptyText.default')}
          </EmptyData>
        ) : (
          children
        )}
      </Padding>
      {isModalOpen && (
        <DashboardPanelModal
          title={title}
          isLoading={isLoading}
          onClose={handleClose}
          children={modalContent ?? children}
        />
      )}
    </ContentWrapper>
  );
};

export interface DashboardPanelModalProps extends PropsWithChildren {
  title: string;
  isLoading: boolean;
  onClose: () => void;
}

export const DashboardPanelModal: FC<DashboardPanelModalProps> = (props) => {
  const { title, isLoading, onClose, children } = props;

  return (
    <Modal
      onCancel={onClose}
      width="60rem"
      loading={isLoading}
      footer={
        <ModalFooter>
          <Button id="close-modal" type="primary" onClick={onClose}>
            {t('close')}
          </Button>
        </ModalFooter>
      }
    >
      <ModalHeader>
        <ModalTitle>
          <TextEllipsis>{title}</TextEllipsis>
        </ModalTitle>
      </ModalHeader>
      <Padding scrollable>
        {children ? children : <EmptyData>{t('emptyText.default')}</EmptyData>}
      </Padding>
    </Modal>
  );
};

const StyledHeader = styled(Padding)`
  border-bottom: 1px solid ${colorGray[2]};
`;

const ContentWrapper = styled(Stack)`
  background-color: ${colorBgContainer};
  border: 1px solid ${colorGray[2]};
  min-height: 15vh;
`;
